import * as React from "react";
import { alpha, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Link, useLocation } from "react-router-dom";
import { Menu, MenuItem, Theme, useMediaQuery, useTheme } from "@mui/material";
import Image from "../../../images/Image";
import { Helmet } from 'react-helmet-async';

// Define the base header height and responsive heights for mobile
const HEADER_HEIGHT = 80; // Default height for larger screens
const MOBILE_HEADER_HEIGHT = 64; // Increased height for mobile screens

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backdropFilter: "blur(24px)",
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
}));

const menuItems = [
  { label: "Challenges", path: "/challenges" },
  { label: "Solutions", path: "/solutions" },

  { label: "Casestudy", path: "/casestudy" },
  { label: "About", path: "/about" },
  {
    label: "Articles",
    path: "",
    children: [
      // { label: "CASESTUDY", path: "/casestudy" },
      { label: "BLOGS", path: "/blogs" },
      { label: "NEWS & REPORT", path: "/news-report" },
    ],
  },
  // { label: "Blogs", path: "/blogs" },
  { label: "Lets Connect", path: "/contact" },
];

interface SubMenuItem {
  label: string;
  path: string;
}

export default function AppMenuBar() {

  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [submenuItems, setSubmenuItems] = React.useState<SubMenuItem[]>([]);
  const location = useLocation();

  const toggleDrawer = (newOpen: boolean) => () => setOpen(newOpen);

  const isActive = (path: string) => location.pathname === path;
  const isTesting = process.env.NODE_ENV === "test";
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const theme = useTheme();
  const isBelowLg = useMediaQuery(theme.breakpoints.down("lg"));

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    children?: SubMenuItem[]
  ) => {
    setAnchorEl(event.currentTarget);
    setSubmenuItems(children || []);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSubmenuItems([]);
  };
const canonicalUrl = typeof window !== 'undefined' 
    ? `https://stonehands.in${window.location.pathname}`
    : 'https://stonehands.in';
  return (
    <>
    <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      {/* Submenu */}
      {submenuItems.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onMouseLeave={handleMenuClose}
          MenuListProps={{
            // onMouseEnter: () => clearTimeout(menuCloseTimer),
            onMouseLeave: handleMenuClose,
          }}
          className="header-submenu-list"
        >
          {submenuItems.map(({ label, path }) => (
            <MenuItem
              key={label}
              component={Link}
               to={`${path}`}
              onClick={handleMenuClose}
            >
              <b> {label}</b>
            </MenuItem>
          ))}
        </Menu>
      )}

      <AppBar
        sx={{
          bgcolor: "#FFFF",
          boxShadow: 0,
          position: "fixed",
          height: { xs: MOBILE_HEADER_HEIGHT, md: HEADER_HEIGHT },
        }}
      >
        <StyledToolbar
          disableGutters
          sx={{
            padding: {
              sm: "20px",
              xs: "0px 20px 0px 20px",
              md: "15px 0px 0px 50px",
              lg: "0px 0px 0px 100px",
              xl: "0px 0px 0px 100px",
            },
          }}
        >
          <Link
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            to={"/"}
            style={{ textDecoration: "none" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",

                width: {
                  xs: "auto",
                  sm: "180px",
                  md: "200px",
                  lg: "250px",
                  xl: "400px",
                },
              }}
            >
              {!isTesting && <Image.HomeLogo />}
            </Box>
          </Link>
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              ml: "auto",
              gap: { xl: 2, md: 1.5, lg: 1.5, sm: 0, xs: 0 },
            }}
            className="headermenu-box"
          >
            {menuItems.map(({ label, path, children }) => (
              <Link
                to={path}
                key={label}
                style={{ textDecoration: "none" }}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <Button
                  className="stonehand-nav-btn"
                  size="small"
                  aria-label="lets connect"
                  onMouseEnter={(e) => handleMenuOpen(e, children)}
                  sx={{
                    borderRadius: 0,
                    pt: 5,
                    pb: 5,
                    pl: { xl: 2.5, md: 1.5, lg: 1.5, sm: 0, xs: 0 },
                    pr: { xl: 2.5, md: 1.5, lg: 1.5, sm: 0, xs: 0 },
                    color:
                      label !== "Lets Connect" && isActive(path)
                        ? "#64E7A5"
                        : label === "Lets Connect"
                        ? "#FFFF"
                        : "#0065FF",
                    "&:hover": {
                      color: "#0065FF",
                    },
                    background: label == "Lets Connect" ? "#0065FF" : "none",
                    "&:focus": {
                      outline: "none",
                    },
                  }}
                >
                  {label}
                </Button>
              </Link>
            ))}
          </Box>
          {isBelowLg && (
            <Link
              to={"/contact "}
              key={"contact"}
              style={{
                textDecoration: "none",
                marginLeft: "auto",
              }}
            >
              <Button
                className="stonehand-nav-btn"
                size="small"
                aria-label="lets connect"
                sx={{
                  fontSize: {
                    xs: "12px",
                    sm: "18px",
                    md: "20px",
                  },
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#0065FF",
                  "&:hover": {
                    color: "#004FCC",
                  },
                }}
              >
                Lets Connect
              </Button>
            </Link>
          )}
          <Box
            sx={{
              display: { xs: "flex", md: "flex", lg: "none" },
              gap: 1,
              ml: { xs: "7px" },
            }}
          >
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{ sx: { top: "var(--template-frame-height, 0px)" } }}
            >
              <Box
                sx={{ p: 2, backgroundColor: "background.default" }}
                className="headermenu-box"
              >
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton aria-label="Close" onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
                {menuItems.map(({ label, path, children }) => (
                  <Box key={label} className="header-menu-items-lists">
                    {/* Main menu item */}
                    {!children ? (
                      <Link to={path} style={{ textDecoration: "none" }}>
                        <MenuItem
                          className="stonehand-nav-btn"
                          sx={{
                            color: "#0065FF",
                          }}
                          onClick={toggleDrawer(false)}
                        >
                          <b>{label}</b>
                        </MenuItem>
                      </Link>
                    ) : (
                      <>
                        {/* Parent label (no link) */}
                        <MenuItem
                          disabled
                          sx={{
                            fontWeight: "bold",

                            color: "#0065FF",
                          }}
                        >
                          {label}
                        </MenuItem>

                        {/* Render submenu items */}
                        {children.map((child) => (
                          <Link
                            to={child.path}
                            key={child.label}
                            style={{ textDecoration: "none", paddingLeft: 16 }}
                          >
                            <MenuItem
                              sx={{
                                pl: 4,
                                color: "#0065FF",
                              }}
                              onClick={toggleDrawer(false)}
                            >
                              {child.label}
                            </MenuItem>
                          </Link>
                        ))}
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </AppBar>
      {/* Main Content */}
      <Box
        sx={{
          marginTop: {
            xs: `${MOBILE_HEADER_HEIGHT}px`,
            md: `${HEADER_HEIGHT}px`,
          },
        }}
      >
        {/* The rest of your content goes here */}
      </Box>
    </>
  );
}
